/**
 * PortionStepper Component
 *
 * Stepper for carb/protein portions with 0.5 increments.
 * Touch targets: w-12 h-12 (48px) exceeds 44px minimum for mobile.
 * Prominent value display in center.
 */

interface PortionStepperProps {
	value: number;
	onChange: (value: number) => void;
	label?: string;
	min?: number;
	max?: number;
	step?: number;
	showGuide?: boolean;
	guideText?: string;
	disabled?: boolean;
}

export function PortionStepper({
	value,
	onChange,
	label = 'Porsi',
	min = 0,
	max = 10,
	step = 0.5,
	showGuide = true,
	guideText = '1 porsi = 1 centong nasi',
	disabled = false,
}: PortionStepperProps) {
	const handleDecrement = () => {
		const newValue = Math.max(min, value - step);
		// Round to avoid floating point issues
		onChange(Math.round(newValue * 10) / 10);
	};

	const handleIncrement = () => {
		const newValue = Math.min(max, value + step);
		// Round to avoid floating point issues
		onChange(Math.round(newValue * 10) / 10);
	};

	const canDecrement = value > min;
	const canIncrement = value < max;

	// Format value for display (show .5 as fraction for readability)
	const displayValue = value % 1 === 0.5 ? `${Math.floor(value)}\u00BD` : value.toString();

	return (
		<div className="space-y-2">
			{label && (
				<label className="block text-sm font-medium text-gray-700">
					{label}
				</label>
			)}
			<div className="flex items-center gap-3">
				{/* Decrement Button */}
				<button
					type="button"
					onClick={handleDecrement}
					disabled={disabled || !canDecrement}
					className="w-12 h-12 flex items-center justify-center text-2xl font-bold text-blue-600 bg-blue-50 rounded-full hover:bg-blue-100 transition disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
					aria-label="Kurangi porsi"
				>
					-
				</button>

				{/* Value Display */}
				<div className="flex-1 text-center">
					<span className="text-3xl font-bold text-gray-900">
						{displayValue}
					</span>
					<span className="ml-1 text-lg text-gray-600">porsi</span>
				</div>

				{/* Increment Button */}
				<button
					type="button"
					onClick={handleIncrement}
					disabled={disabled || !canIncrement}
					className="w-12 h-12 flex items-center justify-center text-2xl font-bold text-blue-600 bg-blue-50 rounded-full hover:bg-blue-100 transition disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
					aria-label="Tambah porsi"
				>
					+
				</button>
			</div>

			{/* Portion Guide */}
			{showGuide && guideText && (
				<p className="text-xs text-gray-500 text-center italic">
					{guideText}
				</p>
			)}
		</div>
	);
}
